import { Button } from '@ai-tutor/ui';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { RoleSelector } from '@/components/features/onboarding/RoleSelector';
import { useCompleteOnboarding, useUserProfile } from '@/hooks/account/useUser';
import type { UserRole } from '@/lib/api-client';
import { notify } from '@/lib/notify';

/**
 * Settings → Learning style card for the role captured at onboarding. Saves
 * through the idempotent `PUT /users/me/onboarding` (via `useCompleteOnboarding`),
 * which overwrites the role and leaves `onboarded` untouched. Lesson depth +
 * flow have their own cards in this same tab.
 */
export function LearningProfileCard() {
  const { data: profile } = useUserProfile();
  const save = useCompleteOnboarding();

  const baseline = profile?.learning_profile?.role ?? undefined;
  const [role, setRole] = useState<UserRole | undefined>(baseline);

  // Re-sync whenever the server profile changes (initial load + post-save).
  useEffect(() => setRole(baseline), [baseline]);

  const isDirty = role !== baseline;

  const handleSave = () => {
    if (!role) return;
    save.mutate(
      { role },
      {
        onSuccess: () => notify.success('Role saved'),
        onError: err => notify.error(err, 'Couldn’t save your role'),
      }
    );
  };

  return (
    <div className='rounded-lg border border-border p-5'>
      <h3 className='mb-1 text-sm font-semibold text-foreground'>Your role</h3>
      <p className='mb-5 text-xs text-muted-foreground'>
        Tells Xonera who you are so lessons and suggestions fit you. Update it
        anytime.
      </p>

      {/* The card's own <h3> above is the heading; `labelHidden` keeps that
          string as the radiogroup's `aria-label` rather than printing it
          twice. */}
      <RoleSelector
        value={role}
        onChange={setRole}
        label='Your role'
        labelHidden
      />

      <div className='mt-6'>
        <Button
          size='sm'
          className='max-sm:h-11'
          onClick={handleSave}
          disabled={!isDirty || save.isPending}
        >
          {save.isPending ? (
            <>
              <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );
}
